/** @format */

import React, {useContext, useState} from 'react';
import {Button} from '@sttark/design-system';
import {
    CartCheckoutContext,
    BILLING_STEP,
    SHIPPING_STEP
} from 'frontier/cart/components/cart/cart_checkout_context';
import './shipping_steps.sass';
import SingleShipment from 'frontier/cart/components/shipping/single_shipment';
import Moment from 'moment';
import SingleShipmentJoinButton from 'frontier/cart/components/shipping/single_shipment_join_button';
import {GRAPHIC_DESIGN_ORDER_TYPES, PRODUCT_TYPE_GRAPHIC_DESIGN} from 'frontier-constants';
import {SingleShipmenGraphicDesign} from 'frontier/cart/components/shipping/single_shipment_graphic_design';
import {ErrorBoundary} from 'frontier-utilities';

export default function CheckoutShippingStep() {
    const ctx = useContext(CartCheckoutContext);

    const isStepComplete = ctx.currentStep > SHIPPING_STEP;

    return (
        <>
            {!isStepComplete && (
                <div className="checkout-order-form">
                    {ctx.shipments.map(({address, ...shipment}, i) => {
                        const relatedIndex = ctx.shipments.findIndex(
                            ({
                                quoted_ship_due_date,
                                address_id,
                                shipping_method_key,
                                product_type_id
                            }) => {
                                return (
                                    product_type_id === shipment.product_type_id &&
                                    Moment(quoted_ship_due_date).format('YYYY-MM-DD') ===
                                        Moment(shipment.quoted_ship_due_date).format('YYYY-MM-DD')
                                );
                            }
                        );
                        if (shipment.product_type_id !== PRODUCT_TYPE_GRAPHIC_DESIGN)
                            return (
                                <React.Fragment key={i}>
                                    <SingleShipment
                                        toShipmentId={ctx.shipments[relatedIndex].id}
                                        relatedShipmentIndex={relatedIndex}
                                        shipment={shipment}
                                        index={i}
                                    />
                                </React.Fragment>
                            );
                        else
                            return (
                                <React.Fragment key={i}>
                                    <SingleShipmenGraphicDesign
                                        toShipmentId={ctx.shipments[relatedIndex].id}
                                        relatedShipmentIndex={relatedIndex}
                                        shipment={shipment}
                                        index={i}
                                    />
                                </React.Fragment>
                            );
                    })}

                    {}
                    <div className="mt-4">
                        <Button
                            onClick={ctx.onSetCurrentStep.bind(this, BILLING_STEP)}
                            label="Save & Continue"
                            primary
                        />
                    </div>
                </div>
            )}

            {isStepComplete && (
                <StepComplete
                    shipments={ctx.shipments}
                    orders={ctx.orders}
                    addresses={ctx.addresses}
                />
            )}
        </>
    );
}

const StepComplete = ({shipments, orders, addresses}) => {
    // get only unique addresses as it each unique address must be represent a row
    let seen = new Set();
    let uniqueAddressShipments = [];

    shipments.forEach(shipment => {
        if (!seen.has(shipment.address_id)) {
            seen.add(shipment.address_id);
            uniqueAddressShipments.push(shipment);
        }
    });

    // get all the shipments to the same addresses to loop over their shipments
    let repeatedAddresses = {};

    shipments.forEach(shipment => {
        if (!repeatedAddresses[shipment.address_id]) {
            repeatedAddresses[shipment.address_id] = [shipment];
        } else {
            repeatedAddresses[shipment.address_id].push(shipment);
        }
    });

    return (
        <div className="mx-0 mt-0 mb-3">
            {uniqueAddressShipments.map(({...shipment}, i) => {
                const [displayAllShipments, setDisplayAllShipments] = useState(false);
                const address = addresses.find(address => shipment.address_id === address.id);

                const shipmentOrderIds = repeatedAddresses[shipment.address_id].map(address =>
                    address.designs_orders_shipments.reduce(
                        (orderIds, {designs_order, ...stuff}) => {
                            if (orderIds.indexOf(designs_order?.order_id) === -1) {
                                return [...orderIds, designs_order?.order_id];
                            }
                            return orderIds;
                        },
                        []
                    )
                );

                const shipsAs = shipments.length > 1 ? 'As Multiple Items' : 'As a Single Item';

                return (
                    <div
                        key={shipment.id}
                        className="w-100 d-flex align-items-start justify-content-start completed-step-meta">
                        <div className="my-0 me-5">
                            <p className="m-0">
                                Ships to: <b>{address.first_name}</b> ({address.company})
                            </p>
                            <p className="m-0">
                                {address.address1} {address.address2}
                            </p>

                            {/* show all the shipments included in this order button*/}
                            {shipmentOrderIds.length > 1 && !displayAllShipments && (
                                <button
                                    onClick={() => setDisplayAllShipments(true)}
                                    className="color-accent-primary border-0 tw-p-0 m-0">
                                    <u>And {shipmentOrderIds.length} more</u>
                                </button>
                            )}

                            {/* list of shipments */}
                            {displayAllShipments && (
                                <div>
                                    {repeatedAddresses[shipment.address_id].map(shipment =>
                                        shipment.orders_shipments.map(order => (
                                            <div key={order?.id} className="mb-2">
                                                <div className="color-accent-primary">
                                                    <u>
                                                        {order?.id} -{' '}
                                                        {order?.customer_label || 'order'}
                                                    </u>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <p className="m-0">
                                Ships: <b>{shipsAs}</b>
                            </p>
                            {shipmentOrderIds.length === 1 && (
                                <p className="m-0">
                                    Estimated arrival:{' '}
                                    <b>{Moment(shipment.quoted_delivery_date).format('MMM D')}</b>
                                </p>
                            )}

                            {shipmentOrderIds.length > 1 && (
                                <p className="m-0">
                                    Items arrive between:{' '}
                                    <b>
                                        {Moment(
                                            repeatedAddresses[shipment.address_id][0]
                                                .quoted_delivery_date
                                        ).format('MMM D')}{' '}
                                        -{' '}
                                        {Moment(
                                            repeatedAddresses[shipment.address_id].at(-1)
                                                .quoted_delivery_date
                                        ).format('MMM D')}
                                    </b>
                                </p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
